import { watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useBillingMe } from "@/features/billing/api/use-billing";

/**
 * AS PORTAS QUE CONTINUAM ABERTAS — o espelho de `PlanGuard.OPEN_DOORS`.
 *
 * O servidor deixa passar `/v3/auth`, `/v3/billing` e `/v3/platform` com o teste
 * vencido, e esta lista são as telas do painel que se alimentam SÓ dessas portas:
 * a pergunta de entrada, as telas de cobrança (fatura, créditos, limites) e o
 * console da instalação. Mandar alguém embora de uma tela que funciona seria a
 * moldura discordando do servidor.
 *
 * As públicas e as `bare` não entram aqui porque não chegam a este composable —
 * quem não tem sessão não tem assinatura para vencer.
 */
const OPEN_DOORS = ["/subscribe", "/billing", "/platform"];

function behindAnOpenDoor(path: string): boolean {
	return OPEN_DOORS.some((door) => path === door || path.startsWith(`${door}/`));
}

/**
 * O TESTE VENCEU — e o painel diz isso UMA vez, em vez de falhar em toda tela.
 *
 * O relógio (`sweepExpiredTrials`) marca a assinatura como `expired` e a partir
 * daí toda rota de produto responde `402 SUBSCRIPTION_EXPIRED`. O que se via
 * antes disto era o pior de dois mundos: cada tela mostrava o seu próprio "não
 * deu para carregar" — que é mentira, não houve falha nenhuma — e a casca
 * repetia o mesmo 402 por cada contador, sem nunca dizer o que aconteceu nem
 * onde se resolve.
 *
 * A decisão NÃO é lida do 402. Vem de `GET /v3/billing/me`, que está numa porta
 * aberta de propósito: sobrevive a um recarregar (um 402 só existe depois de
 * uma chamada falhar) e responde antes de a tela tentar. O 402 continua a acontecer e agora tem nome próprio
 * (`kind: "paymentRequired"`), para o caso de alguma chamada correr à frente
 * desta.
 *
 * `replace` e não `push`: a tela que não carregou não merece uma entrada no
 * histórico, e voltar para ela seria voltar para a parede. `de` viaja para que
 * escolher o plano devolva a pessoa exatamente onde ela estava — o `PlanGuard`
 * lê a assinatura a cada pedido, então a reativação vale no segundo seguinte.
 */
export function useTrialWall(enabled: () => boolean): void {
	const route = useRoute();
	const router = useRouter();
	const { data, isFetching } = useBillingMe(enabled);

	watchEffect(() => {
		if (!enabled()) return;
		// Enquanto a resposta está a caminho, o que está em mãos é a de antes.
		// Escolher um plano invalida `billing/me` e navega de volta no mesmo
		// instante: sem esta linha, a leitura velha ("expirado") empurraria a pessoa
		// de volta para os planos meio segundo depois de ela ter escolhido um.
		if (isFetching.value) return;
		if (data.value?.subscription?.status !== "expired") return;
		if (behindAnOpenDoor(route.path)) return;
		void router.replace({ name: "subscribe", query: { de: route.fullPath } });
	});
}
